import { Component, ViewContainerRef, computed, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink, RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AccountService } from '@app/service/account.service';
import { MainI18nService } from '@app/service/i18n.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { LanguageChangeComponent } from '@app/common/dialog/language/change/language.change.component';
import { ThemeChangeComponent } from '@app/common/dialog/theme/change/theme.change.component';
import { OrganizationChangeComponent } from '@app/common/dialog/organization/change/organization.change.component';
import { persistedBool } from '@app/common/util/storage/persisted-signal';
import { UartBarComponent } from './uart-bar/uart-bar.component';
import pkg from '../../../../package.json';
import { NzSpaceModule } from 'ng-zorro-antd/space';

/** 侧栏收缩状态落 `localStorage` 的键。同 `uart.options` 的口径：裸键、点号分区。 */
const COLLAPSED_KEY = 'main.sidebarCollapsed';

/** 窄屏阈值。沿用原来 `nzBreakpoint="md"` 的口径（ng-zorro 的 md 就是 768px）。 */
const NARROW_MAX = 768;

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrl: './main.component.less',
  imports: [
    RouterLink,
    RouterOutlet,
    NzIconModule,
    NzLayoutModule,
    NzMenuModule,
    NzAvatarModule,
    TranslatePipe,
    UartBarComponent,
    NzSpaceModule,
  ],
  providers: [NzModalService],
})
export class MainComponent {
  /**
   * 侧栏是否收起，**记住上一次的选择**：下次进页面还是上次那样。
   *
   * <p>这件事原来是交给 `nz-sider` 的 `nzBreakpoint="md"` 的，那条路必须让开：
   * 它的实现是 `breakpointService.subscribe(map, true)` —— 第二个参数让**初始化当拍就出一次值**，
   * 紧接着 `setCollapsed()` 按窗口宽度强推一次。于是「存下来的偏好」在每次加载时都会被冲掉，
   * 宽屏一律被推成展开，记了等于没记。</p>
   *
   * <p>去掉之后默认值自己定：**没存过**才看窗口宽度（窄屏收起、宽屏展开，与原来 md 的表现一致），
   * 存过就一律听存的那份。代价是窗口从宽拖到窄不再自动收起 —— Web Serial 只有桌面 Chromium 提供，
   * 窄屏本来就不是这一页的场子，而「记住了还自己变」比「不自动收」更让人意外。</p>
   */
  protected readonly collapsed = persistedBool(COLLAPSED_KEY, window.innerWidth < NARROW_MAX);

  version: string = pkg.version;

  /**
   * 品牌名的 i18n key：侧栏 logo、浏览器标题与登录页页头三处共用。
   *
   * <p>**只有三个中文包有这条词条**（`zh` / `zh_HK` / `zh_TW`，值都是「串口助手」），
   * 其余 63 个包没有。`MainI18nService` 没有设过 `setDefaultLang` / `setFallbackLang`，
   * ngx-translate 查不到时原样返回 key，于是非中文语言下就显示 `MyUart` —— 品牌名本来就该如此。
   * 哪天真要给它做各语言的正式译名，往对应的 `public/i18n/*.json` 里补一条即可，这里不用动。</p>
   */
  protected static readonly BRAND_KEY = 'MyUart';

  private readonly i18n = inject(MainI18nService);
  private readonly modal = inject(NzModalService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly title = inject(Title);

  /**
   * 当前账号。表头右侧据此切换「头像 + 用户名 / 登录」。
   *
   * <p>`protected` 是因为模板要读它 —— 组件模板与组件类是同一个模板作用域，这里不用 `public`。
   * 之所以不另开一个 `login` 布尔量：`account.login()` 本身就是个 signal，
   * 模板里直接读它就会随登录/退出自动重渲染。</p>
   */
  protected readonly account = inject(AccountService);

  /** 语言切换信号：品牌名走翻译，切语言时要重算标题 */
  private readonly langChange = toSignal(inject(TranslateService).onLangChange);

  /** 当前标题：品牌名。侧栏 logo 与浏览器标签页共用。 */
  protected readonly currentTitle = computed(() => {
    this.langChange();
    return this.i18n.translate.instant(MainComponent.BRAND_KEY);
  });

  constructor() {
    // 同步浏览器标签页标题
    effect(() => {
      this.title.setTitle(this.currentTitle());
    });
  }

  protected changeLanguage() {
    this.modal.create<LanguageChangeComponent, string, string>({
      nzTitle: '',
      nzWidth: 1400,
      nzContent: LanguageChangeComponent,
      nzViewContainerRef: this.viewContainerRef,
      nzData: '',
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: true,
      nzKeyboard: true,
    });
  }

  /**
   * 换主题。与 {@link changeLanguage} 同一套写法，只是窄一些 —— 八张卡铺成 4 × 2（每张 6/24 格），
   * 720px 正好装下四列，不必像语言那样占满一屏（那边是几十种语言的长列表）。
   */
  protected changeTheme() {
    this.modal.create<ThemeChangeComponent, string, string>({
      nzTitle: '',
      nzWidth: 720,
      nzContent: ThemeChangeComponent,
      nzViewContainerRef: this.viewContainerRef,
      nzData: '',
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: true,
      nzKeyboard: true,
    });
  }

  /**
   * 点顶栏「账号」：弹**组织切换**对话框（与语言/主题同一套写法）。组织列表见
   * {@link OrganizationChangeComponent}；点卡片切当前组织并回首页，让切换立即生效。
   */
  protected changeOrganization() {
    this.modal.create<OrganizationChangeComponent, string, string>({
      nzTitle: '',
      nzWidth: 720,
      nzContent: OrganizationChangeComponent,
      nzViewContainerRef: this.viewContainerRef,
      nzData: '',
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: true,
      nzKeyboard: true,
    });
  }
}
